import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Pencil, Trash2 } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Table } from '@components/base/data';
import { HStack } from '@components/base/layout';
import { useAdminListPage } from '../../hooks/useAdminListPage';
import { AdminListPage } from '../../components/AdminListPage';
import { usersService } from '../../api/usersService';
import type { User } from '../../api/usersService';
import { ResetPasswordModal } from './ResetPasswordModal';

export function UsersListPage() {
  const navigate = useNavigate();
  const [resetTarget, setResetTarget] = useState<User | null>(null);

  const listPage = useAdminListPage<User, number | string>({
    queryKey: ['admin', 'users'],
    queryFn: () => usersService.list(),
    deleteFn: (id) => usersService.remove(id),
    getDeleteId: (user) => user.id,
  });

  return (
    <>
      <AdminListPage<User>
        title="Users"
        subtitle="Manage your application users"
        loadingLabel="Loading users..."
        errorTitle="Failed to load users"
        errorFallback="An unexpected error occurred while loading users."
        emptyTitle="No users found"
        emptyDescription="Get started by creating your first user."
        createLabel="Create User"
        onCreateClick={() => navigate('/admin/users/create')}
        items={listPage.items}
        getRowKey={(user) => user.id}
        isLoading={listPage.isLoading}
        error={listPage.error}
        deleteModalTitle="Delete User"
        deleteTarget={listPage.deleteTarget}
        getDeleteTargetName={(user) => user.name}
        deleteNoun="user"
        onDeleteCancel={listPage.cancelDelete}
        onDeleteConfirm={listPage.confirmDelete}
        isDeleting={listPage.isDeleting}
        renderHead={() => (
          <>
            <Table.HeaderCell>Name</Table.HeaderCell>
            <Table.HeaderCell>Email</Table.HeaderCell>
            <Table.HeaderCell>Roles</Table.HeaderCell>
            <Table.HeaderCell align="center">Email Verified</Table.HeaderCell>
            <Table.HeaderCell align="right">Actions</Table.HeaderCell>
          </>
        )}
        renderRow={(user) => (
          <>
            <Table.Cell>
              <Text weight="medium">{user.name}</Text>
            </Table.Cell>
            <Table.Cell>
              <Text size="sm" color="muted">
                {user.email}
              </Text>
            </Table.Cell>
            <Table.Cell>
              <HStack gap={1} wrap>
                {user.roles.length > 0 ? (
                  user.roles.map((role) => (
                    <Badge key={role} variant="info" size="sm">
                      {role}
                    </Badge>
                  ))
                ) : (
                  <Text size="sm" color="muted">
                    --
                  </Text>
                )}
              </HStack>
            </Table.Cell>
            <Table.Cell align="center">
              <Badge
                variant={user.email_verified_at ? 'success' : 'default'}
                size="sm"
              >
                {user.email_verified_at ? 'Verified' : 'Unverified'}
              </Badge>
            </Table.Cell>
            <Table.Cell align="right">
              <HStack gap={1} justify="end">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Reset password for ${user.name}`}
                  leftIcon={<Icon icon={KeyRound} size="sm" />}
                  onClick={() => {
                    setResetTarget(user);
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Edit ${user.name}`}
                  leftIcon={<Icon icon={Pencil} size="sm" />}
                  onClick={() =>
                    navigate(`/admin/users/${user.id}/edit`)
                  }
                />
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Delete ${user.name}`}
                  leftIcon={<Icon icon={Trash2} size="sm" color="error" />}
                  onClick={() => listPage.setDeleteTarget(user)}
                />
              </HStack>
            </Table.Cell>
          </>
        )}
      />
      <ResetPasswordModal
        user={resetTarget}
        onClose={() => {
          setResetTarget(null);
        }}
      />
    </>
  );
}
